import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, timeout, toArray } from 'rxjs/operators';
import { gql } from 'apollo-angular';
import { normalizeGtin } from '../../../common/utils/gtin.utils';
import { DataService } from '../../providers/data/data.service';
import { compareProduct, ComparisonRow, ListProduct, SavedProductList } from './list-page.models';

const RESOLVE_LIST_GTINS = gql`
    query ResolveListGtins($input: SearchInput!) {
        search(input: $input) { items { productId slug productName customProductMappings { gtin } } totalItems }
    }
`;
const LIST_COMPARISON_PRODUCT = gql`
    query GetListComparisonProduct($slug: String!) {
        product(slug: $slug) {
            id name enabled
            customFields { gtin brand quantityPerBox }
            featuredAsset { preview }
            variants {
                id price priceWithTax moq
                stockLevels { stockOnHand stockAllocated }
                pricing { id currency validFrom validTo fullPackageOnly }
                pricingProduct { minQ maxQ }
            }
        }
    }
`;
interface Match { productId: string; slug: string; productName: string; customProductMappings?: { gtin?: string }; }
@Injectable({ providedIn: 'root' })
export class SavedListsService {
    constructor(private data: DataService) {}
    read(): Observable<SavedProductList[]> {
        return this.data.query<{ customerProductLists: SavedProductList[] }>(gql`
            query CustomerProductLists { customerProductLists { id name updatedAt revision products { id name slug gtin targetPrice quantity } } }
        `, {}, 'network-only').pipe(take(1), timeout(20000), map(result => result.customerProductLists));
    }
    private input(list: SavedProductList): { name: string; products: unknown[] } {
        return { name: list.name, products: list.products.map(p => ({ id: p.id,
            ...(!p.gtin && !/^[1-9]\d*$/.test(p.id) && p.slug ? { slug: p.slug } : {}),
            gtin: p.gtin || null, targetPrice: p.targetPrice ?? null, quantity: p.quantity ?? null })) };
    }
    create(list: SavedProductList): Observable<SavedProductList> {
        return this.data.mutate<{ createCustomerProductList: SavedProductList }>(gql`
            mutation CreateCustomerProductList($input: CustomerProductListInput!) {
                createCustomerProductList(input: $input) { id name updatedAt revision products { id name slug gtin targetPrice quantity } }
            }
        `, { input: this.input(list) }).pipe(take(1), map(result => result.createCustomerProductList));
    }
    update(list: SavedProductList): Observable<SavedProductList> {
        return this.data.mutate<{ updateCustomerProductList: SavedProductList }>(gql`
            mutation UpdateCustomerProductList($id: ID!, $revision: Int!, $input: CustomerProductListInput!) {
                updateCustomerProductList(id: $id, revision: $revision, input: $input) { id name updatedAt revision products { id name slug gtin targetPrice quantity } }
            }
        `, { id: list.id, revision: list.revision, input: this.input(list) }).pipe(take(1), map(result => result.updateCustomerProductList));
    }
    remove(list: SavedProductList): Observable<boolean> {
        return this.data.mutate<{ deleteCustomerProductList: boolean }>(gql`
            mutation DeleteCustomerProductList($id: ID!, $revision: Int!) { deleteCustomerProductList(id: $id, revision: $revision) }
        `, { id: list.id, revision: list.revision }).pipe(take(1), map(result => result.deleteCustomerProductList));
    }
    compare(list: SavedProductList): Observable<ComparisonRow[]> {
        const codes = [...new Set(list.products.filter(p => !p.slug && p.gtin).map(p => p.gtin!))];
        return this.resolve(codes).pipe(switchMap(({ matches, failed }) => from(list.products.map((saved, index) => ({ saved, index }))).pipe(
            mergeMap(({ saved, index }) => {
                const match = saved.gtin ? matches.get(normalizeGtin(saved.gtin)) : null;
                const slug = saved.slug || match?.slug;
                if (!slug) return of({ row: compareProduct(saved, null, failed.has(normalizeGtin(saved.gtin))), index });
                return this.data.query<{ product: ListProduct | null }>(LIST_COMPARISON_PRODUCT, { slug }, 'network-only').pipe(
                    take(1), timeout(20000), map(({ product }) => ({ row: compareProduct(saved, product ? { ...product, slug } : null), index })),
                    catchError(() => of({ row: compareProduct(saved, null, true), index })));
            }, 4), toArray(), map(results => results.sort((a, b) => a.index - b.index).map(result => result.row)),
        )));
    }
    private resolve(codes: string[]): Observable<{ matches: Map<string, Match>; failed: Set<string> }> {
        if (!codes.length) return of({ matches: new Map(), failed: new Set() });
        const chunks: string[][] = [];
        for (let i = 0; i < codes.length; i += 50) chunks.push(codes.slice(i, i + 50));
        return from(chunks).pipe(mergeMap(chunk => this.data.query<{ search: { items: Match[]; totalItems: number } }>(RESOLVE_LIST_GTINS,
            { input: { term: chunk.join(','), groupByProduct: true, take: 100 } }, 'network-only').pipe(take(1), timeout(20000),
                map(result => ({ items: result.search.items, failed: result.search.totalItems > result.search.items.length ? chunk : [] })),
                catchError(() => of({ items: [] as Match[], failed: chunk }))), 2), toArray(), map(results => {
                    const matches = new Map<string, Match>(); const failed = new Set<string>();
                    for (const result of results) {
                        result.failed.forEach(code => failed.add(normalizeGtin(code)));
                        for (const item of result.items) if (item.customProductMappings?.gtin) matches.set(normalizeGtin(item.customProductMappings.gtin), item);
                    }
                    return { matches, failed };
                }));
    }
}
